import React, { useMemo } from "react";
import shortid from "shortid";
import produce from "immer";

import {
  Product,
  Variant,
  ProductStock,
  VariantOptionStock,
} from "../product/types";

import {
  CartItem,
  Context,
  State,
  Actions,
  Cart,
  CheckoutResponse,
  CostValues,
  ProductPriceCheck,
  ProductPriceCheckItem,
  ProductPriceMismatch,
} from "./types";
import { getOrderId, getTotal } from "./selectors";
import api from "./api";
import { readCart, writeCart, clearCart, reconcileCart } from "./persistence";

import ordersApi from "~/order/api/client";
import { Field } from "~/tenant/types";
import { useAnalytics } from "~/analytics/hooks";
import { useTenant } from "~/tenant/hooks";
import { useProducts } from "~/product/hooks";
import { isPremiumPlan } from "~/tenant/selectors";
import { isStoreOpen as computeStoreOpen } from "~/tenant/utils/availableHours";
import paymentApi from "~/payment/api/client";
import date from "~/utils/date";
import { CURRENCIES } from "@pency/constants/catalogs/i18n";
import { useTranslation } from "~/i18n/hooks";
import { getPencyFee } from "./utils";
import { getMessage } from "~/order/selectors";
import { PaymentMethods } from "@pency/api/orders/types";
import { paymentStatusConstants } from "@pency/api/orders/controllers/patch/types";
interface Props {
  children: JSX.Element | JSX.Element[];
}

const CartContext = React.createContext({} as Context);

const CartProvider = ({ children }: Props) => {
  const log = useAnalytics();
  //const tenant = useTenant()
  const {
    phone,
    slug,
    flags,
    mercadopago,
    country,
    id,
    tier,
    createdAt,
    availableHours,
  } = useTenant();
  const hideDecimals = Boolean(flags?.includes("hideDecimals"));
  const [cart, setCart] = React.useState<Cart>({});
  const items = useMemo(() => Object.values(cart), [cart]);
  const t = useTranslation();
  const products = useProducts();
  const [reconcileNotice, setReconcileNotice] = React.useState("");
  // Evita que el primer render (carrito vacío) pise el carrito guardado antes
  // de hidratarlo desde localStorage.
  const skipFirstPersist = React.useRef(true);

  // Hidratación: al montar levantamos el carrito guardado y lo reconciliamos
  // contra los productos frescos de la página (precios de producto y variantes,
  // productos que dejaron de estar disponibles).
  React.useEffect(() => {
    if (!id) return;

    const stored = readCart(id);
    if (!stored) return;

    const { cart: reconciled, removed, priceChanged } = reconcileCart(
      stored,
      products
    );
    setCart(reconciled);

    if (removed.length) {
      setReconcileNotice(t("products.removedItemsMessage"));
    } else if (priceChanged) {
      setReconcileNotice(t("products.priceReloadMessage"));
    }
    // Corremos una sola vez por tenant, con los productos del server.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Persistencia: guardamos el carrito ante cada cambio, salteando el primer
  // render vacío para no pisar lo guardado antes de hidratar.
  React.useEffect(() => {
    if (!id) return;
    if (skipFirstPersist.current) {
      skipFirstPersist.current = false;
      return;
    }
    writeCart(id, cart);
  }, [cart, id]);

  function dismissReconcileNotice() {
    setReconcileNotice("");
  }

  const isScheduleBlocking = () =>
    isPremiumPlan(tier) && !computeStoreOpen(availableHours);

  //async function getDaysAccreditation() {
  //  const daysAccreditation = await paymentApi.getDaysAccreditation(id)
  //  return daysAccreditation
  //}
  function add(
    product: Product,
    variants: Variant[],
    count: number = 1,
    note: string = ""
  ) {
    if (isScheduleBlocking()) return;

    log.addToCart(product);

    return setCart(
      produce((cart) => {
        const id = shortid.generate();

        cart[id] = {
          id,
          variants,
          count,
          product,
          note,
        };
      })
    );
  }

  function remove(id: CartItem["id"]) {
    if (!cart[id]) return;

    log.removeFromCart(cart[id].product);

    return setCart(
      produce((cart) => {
        delete cart[id];
      })
    );
  }

  function increase(id: CartItem["id"]) {
    if (!cart[id]) return;
    if (isScheduleBlocking()) return;

    return setCart(
      produce((cart) => {
        cart[id].count++;
      })
    );
  }

  function increaseMany(id: CartItem["id"], count: number) {
    if (!cart[id]) return;
    if (isScheduleBlocking()) return;

    return setCart(
      produce((cart) => {
        cart[id].count += count;
      })
    );
  }

  function decrease(id: CartItem["id"]) {
    if (!cart[id]) return;

    if (cart[id].count === 1) {
      return remove(id);
    }

    return setCart(
      produce((cart) => {
        cart[id].count--;
      })
    );
  }

  function handleOrder(orderPrices?: CostValues) {
    // We generate an order id
    const orderId = getOrderId(slug);
    // Get total price
    const total = orderPrices ? orderPrices.cost : getTotal(items);
    // Generate order before saving it
    const order = {
      _id: orderId,
      tenantId: id,
      items,
      totalPrice: total,
      currency: CURRENCIES[country as keyof typeof CURRENCIES],
      state: "PENDING",
      coupon: orderPrices?.couponCode
        ? orderPrices.couponCode.toUpperCase()
        : undefined,
      priceVar: {
        delivery: orderPrices?.delivery ?? 0,
        financial: orderPrices?.financial ?? 0,
        opt: orderPrices?.opt ?? "message.noCost",
      },
    };

    return order;
  }

  const validatePhone = (phone: string) => {
    if (createdAt > 1659996114201 && createdAt < 1662692400000) {
      if (phone.startsWith("+")) {
        return phone;
      } else {
        if (country === "AR") {
          if (phone.startsWith("54")) {
            return phone;
          }
          return `+54${phone}`;
        }
        if (country === "CO") {
          if (phone.startsWith("57")) {
            return phone;
          }
          return `+57${phone}`;
        }
        if (country === "MX") {
          if (phone.startsWith("52")) {
            return phone;
          }
          return `52${phone}`;
        }
        if (country === "GT") {
          if (phone.startsWith("502")) {
            return phone;
          }
          return `502${phone}`;
        }
        if (country === "BO") {
          if (phone.startsWith("591")) {
            return phone;
          }
          return `591${phone}`;
        }
        if (country === "BR") {
          if (phone.startsWith("55")) {
            return phone;
          }
          return `55${phone}`;
        }
        if (country === "CL") {
          if (phone.startsWith("56")) {
            return phone;
          }
          return `56${phone}`;
        }
        if (country === "PE") {
          if (phone.startsWith("51")) {
            return phone;
          }
          return `51${phone}`;
        }
        if (country === "CR") {
          if (phone.startsWith("506")) {
            return phone;
          }
          return `506${phone}`;
        }
        if (country === "CU") {
          if (phone.startsWith("53")) {
            return phone;
          }
          return `53${phone}`;
        }
        if (country === "DO") {
          if (phone.startsWith("1809")) {
            return phone;
          }
          return `1809${phone}`;
        }
        if (country === "EC") {
          if (phone.startsWith("593")) {
            return phone;
          }
          return `593${phone}`;
        }
        if (country === "ES") {
          if (phone.startsWith("34")) {
            return phone;
          }
          return `34${phone}`;
        }
        // Panama
        if (country === "PA") {
          if (phone.startsWith("507")) {
            return phone;
          }
          return `507${phone}`;
        }
        if (country === "PY") {
          if (phone.startsWith("595")) {
            return phone;
          }
          return `595${phone}`;
        }
        if (country === "SV") {
          if (phone.startsWith("503")) {
            return phone;
          }
          return `503${phone}`;
        }
        if (country === "US") {
          if (phone.startsWith("1")) {
            return phone;
          }
          return `1${phone}`;
        }
        // Uruguay
        if (country === "UY") {
          if (phone.startsWith("598")) {
            return phone;
          }
          return `598${phone}`;
        }
        // Venezuela
        if (country === "VE") {
          if (phone.startsWith("58")) {
            return phone;
          }
          return `58${phone}`;
        }
        return phone;
      }
    }
    return phone;
  };

  async function checkout(
    fields?: Field[],
    orderPrices?: CostValues,
    paymentMethod: PaymentMethods = PaymentMethods.other,
    couponInfo?: { code: string; discount: number; subtotal: number } | null
  ): Promise<CheckoutResponse> {
    if (isScheduleBlocking()) {
      return Promise.reject({ reason: "STORE_CLOSED" });
    }

    // Validamos precios contra la DB ANTES de crear la preferencia de MercadoPago
    // y de guardar la orden. Si el cliente tenía precios viejos (ISR), parcheamos
    // el carrito con los precios frescos, frenamos el checkout y pedimos reconfirmar.
    // Fail-open: si el chequeo NO se pudo ejecutar (error de red/servidor), dejamos
    // pasar el checkout. Solo frenamos ante una diferencia REAL de precios.
    const priceCheckResult = await api
      .checkPrices(getProductPricesFromOrderItems(), id)
      .catch(() => null);

    if (!priceCheckResult) {
      // No pudimos validar precios (error de red/servidor): no bloqueamos la venta.
      console.warn(
        "No se pudieron validar precios; se continúa el checkout (fail-open)."
      );
    } else if (!priceCheckResult.success) {
      const priceValidation = priceCheckResult.validationData ?? {
        isPriceOk: false,
        details: [],
      };

      if (priceValidation.details?.length) {
        applyFreshPrices(priceValidation.details);
      }

      return {
        isValidationOk: false,
        isError: false,
        redirectUrl: "",
        orderDetails: "",
        paymentIndicator: PaymentMethods.other,
        priceValidation,
      };
    }

    // We generate an order id
    const orderId = getOrderId(slug);

    const validPhone = validatePhone(phone);

    // Store preference
    let preference = null;

    const total = orderPrices ? orderPrices.cost : getTotal(items);
    // Log to analytics
    log.checkout(total);
    // We store order prices to use when neccesary
    const mapPrices: CostValues = {
      cost: orderPrices ? orderPrices.cost : total,
      delivery: orderPrices ? orderPrices.delivery : 0,
      financial: orderPrices ? orderPrices.financial : 0,
      opt: orderPrices ? orderPrices.opt : "message.noCost",
      couponCode: orderPrices?.couponCode,
    };
    // We create preference by paying with Mercado Pago
    if (mercadopago && paymentMethod !== PaymentMethods.other) {
      try {
        // Add Pency fee depending of tier
        const priceVar = {
          ...mapPrices,
          pencyFee: getPencyFee(tier, mapPrices.cost),
        };
        //const applyFee = tenant.flags?.includes("termsAndConditions")
        // Create a preference for this items
        preference = await paymentApi.create(
          slug,
          items,
          orderId,
          priceVar,
          country!,
          tier
        );
      } catch (e) {
        // If we had an error log it to the console
        console.warn("Error generando preferencia de MercadoPago: ", e);
      }
    }

    //We check if the current products have a valid stock for the current order.
    const updateStockProducts = getProductStockFromOrderItems();
    const stockCheckResponse = await checkStock();

    //If all the stocks are ok then we proceed.
    if (stockCheckResponse.isValidationOk) {
      //We saved the order in DB
      try {
        await ordersApi.saveOrder(
          items,
          orderId,
          id,
          date.now,
          CURRENCIES[country as keyof typeof CURRENCIES],
          mapPrices,
          fields!,
          paymentMethod,
          t,
          tier
        );
      } catch (saveError) {
        // Backstop autoritativo: el server rechazó por precios desactualizados.
        // Parcheamos el carrito y devolvemos el detalle para reconfirmar, sin
        // redirigir ni continuar el checkout.
        const priceValidation = (saveError as {
          priceValidation?: ProductPriceCheck | null;
        })?.priceValidation;

        if (priceValidation) {
          if (priceValidation.details?.length) {
            applyFreshPrices(priceValidation.details);
          }
          return {
            isValidationOk: false,
            isError: false,
            redirectUrl: "",
            orderDetails: "",
            paymentIndicator: PaymentMethods.other,
            priceValidation,
          };
        }
        throw saveError;
      }
      //Then we send the order to the Pency Ticket App
      // If a webhook is configured
      if (flags?.includes("orders")) {
        try {
          // Do a post to it
          ordersApi.create(slug, {
            items,
            orderId,
            t,
            mapPrices,
            fields,
            preference,
            couponInfo,
            country,
          });
        } catch (e) {
          // If we had an error log it to the console
          console.log("Hook was not dispatched: ", e);
        }
      }
      // Then we update the stock and redirect to whatsapp
      await api.updateStock(updateStockProducts, id).then(() => {
        // Get URL to handle redirect response
        const redirect: string = api.checkout({
          phone: validPhone,
          items,
          orderId,
          t,
          mapPrices,
          couponInfo,
          fields,
          state: paymentStatusConstants.PAYMENT_PENDING.label,
          preference,
          country,
          tier,
          hideDecimals,
        });
        // Get order details message to create order page
        const orderDetails = getMessage(
          items,
          orderId,
          t,
          mapPrices,
          couponInfo,
          fields,
          paymentStatusConstants.PAYMENT_PENDING.label,
          preference ?? undefined,
          country,
          tier,
          hideDecimals
        );
        // We only persist preference when payment is via checkoutPRO
        if (paymentMethod !== PaymentMethods.checkoutPro) preference = null;
        // We set Url depeding of the payment method - preference(MP)/redirect(other)
        stockCheckResponse.redirectUrl = preference ? preference : redirect;
        // we set payment method to validate operation
        stockCheckResponse.paymentIndicator = paymentMethod && paymentMethod;
        // We save the order raw text to display it to the user
        stockCheckResponse.orderDetails = orderDetails;
      });

      // Orden guardada y confirmada → limpiamos el carrito persistido para que,
      // al volver a la tienda, no reaparezca el carrito ya comprado.
      clearCart(id);
    } else {
      const cartCopy = Object.values(cart);
      cartCopy.forEach((itemInCart) => {
        stockCheckResponse.validationData?.details?.forEach((orderProduct) => {
          if (itemInCart.product.id === orderProduct.id) {
            if (!cart[itemInCart.id]) return;

            if (orderProduct.currentStock === 0) {
              return null;
            } else {
              return setCart(
                produce((cart) => {
                  cart[itemInCart.id].count = orderProduct.currentStock;
                })
              );
            }
          }
        });
      });
    }
    return stockCheckResponse;
  }

  async function checkStock(): Promise<CheckoutResponse> {
    //First we create a smaller object for the update stock process.

    const updateStockProducts = getProductStockFromOrderItems();

    //We check if the current products have a valida stock for the current order.

    let response: CheckoutResponse = {
      isValidationOk: false,
      isError: false,
      redirectUrl: "",
      orderDetails: "",
      paymentIndicator: PaymentMethods.other,
    };

    await checkStockFromAPI(updateStockProducts, response);

    return response;
  }

  //Functions used into this class.
  async function checkStockFromAPI(
    updateStockProducts: ProductStock[],
    response: CheckoutResponse
  ) {
    try {
      const returned = await api.checkStock(updateStockProducts, id);
      const { success, validationData } = returned;
      response.isValidationOk = success;

      if (!success) {
        response.validationData = validationData;
      }
      return response;
    } catch (error) {
      // Fail-open: si el chequeo de stock no se pudo ejecutar (error de red/servidor),
      // no bloqueamos la venta. Solo se frena ante una falta de stock REAL.
      console.warn(
        "No se pudo validar stock; se continúa el checkout (fail-open):",
        error
      );
      response.isValidationOk = true;
      response.isError = true;
      response.error = error;
      return response;
    }
  }

  function getProductStockFromOrderItems() {
    return items.map((orderProduct) => {
      // Collect variant option stock info
      const variantOptionsStock: VariantOptionStock[] = [];

      if (orderProduct.variants?.length) {
        for (const variant of orderProduct.variants) {
          if (variant.value?.length) {
            // Group selected options by id to count how many times each is selected
            const optionCounts: Record<string, number> = {};
            for (const option of variant.value) {
              if (option.handleStock) {
                optionCounts[option.id] = (optionCounts[option.id] || 0) + 1;
              }
            }
            // Create stock entries for each unique option
            for (const [optionId, count] of Object.entries(optionCounts)) {
              variantOptionsStock.push({
                variantId: variant.id,
                optionId,
                orderStock: count * orderProduct.count,
              });
            }
          }
        }
      }

      const productStock: ProductStock = {
        id: orderProduct.product.id,
        updatedAt: date.now,
        orderStock: orderProduct.count,
        ...(variantOptionsStock.length > 0 && {
          options: variantOptionsStock,
        }),
      };

      return productStock;
    });
  }

  // Arma el payload con los precios que el cliente "cree" tener (pueden estar
  // viejos si dejó la pestaña abierta y nunca se revalidó el ISR).
  function getProductPricesFromOrderItems(): ProductPriceCheckItem[] {
    return items.map((item) => {
      const seen = new Set<string>();
      const options: ProductPriceCheckItem["options"] = [];

      item.variants?.forEach((variant) => {
        variant.value?.forEach((option) => {
          const key = `${variant.id}:${option.id}`;
          if (seen.has(key)) return;
          seen.add(key);
          options.push({
            variantId: variant.id,
            optionId: option.id,
            price: option.price,
          });
        });
      });

      return {
        id: item.product.id,
        price: item.product.price,
        ...(options.length ? { options } : {}),
      };
    });
  }

  // Parchea en el carrito (en memoria) los precios que cambiaron, usando los
  // valores frescos que devolvió la DB. Mismo enfoque que el ajuste de stock:
  // no recargamos la página para no perder el carrito.
  function applyFreshPrices(details: ProductPriceMismatch[]) {
    setCart(
      produce((cart: Cart) => {
        Object.values(cart).forEach((item) => {
          const detail = details.find((d) => d.id === item.product.id);
          if (!detail) return;

          if (typeof detail.currentPrice === "number") {
            item.product.price = detail.currentPrice;
          }

          detail.options?.forEach((freshOption) => {
            item.variants?.forEach((variant) => {
              if (variant.id !== freshOption.variantId) return;
              variant.value?.forEach((option) => {
                if (option.id === freshOption.optionId) {
                  option.price = freshOption.currentPrice;
                }
              });
            });
          });
        });
      })
    );
  }

  const state: State = { items, cart, reconcileNotice };
  const actions: Actions = {
    add,
    remove,
    checkout,
    checkStock,
    increase,
    increaseMany,
    decrease,
    handleOrder,
    dismissReconcileNotice,
  };

  return (
    <CartContext.Provider value={{ state, actions }}>
      {children}
    </CartContext.Provider>
  );
};

export { CartProvider as Provider, CartContext as default };
